import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { updateQueue } from 'flow/actions/pbx-actions';
import { selectQueueDescription, selectQueueEditable, selectQueueKeywords } from 'flow/selectors/queue-selectors';
import { routes } from 'flow/types/routes';
import { generatePath } from 'flow/utils/router-utils';
import Page from '../Setting/Page';
import BaseInput from '../ui-components/BaseInput';
import Loading from '../ui-components/Loading';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var NAME_INPUT_ID = 'pbx-settings-name';
var KEYWORDS_INPUT_ID = 'pbx-settings-keywords';

/**
 * Name and keywords are edited on one page, which is what PD-2455 asks for: "move the
 * field for search word/keywords into the area of editing of the queue name".
 *
 * The name is the queue's own `description`, while keywords sit on the PBX service's
 * contact. Both save together in one updateQueue call, and only the fields that changed
 * are sent.
 */
var PbxSettingsEditName = function PbxSettingsEditName() {
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var dispatch = useAppDispatch();
  var history = useHistory();
  var _useParams = useParams(),
    entityKey = _useParams.entityKey;
  var description = useAppSelector(function (state) {
    return selectQueueDescription(state, entityKey);
  });
  var keywords = useAppSelector(function (state) {
    return selectQueueKeywords(state, entityKey);
  });
  var editable = useAppSelector(function (state) {
    return selectQueueEditable(state, entityKey);
  });
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    newName = _useState2[0],
    setNewName = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    newKeywords = _useState4[0],
    setNewKeywords = _useState4[1];
  var onClickBack = function onClickBack() {
    var keywordsChanged = newKeywords !== null && newKeywords !== (keywords !== null && keywords !== void 0 ? keywords : '');

    // Name and keywords go in one call, to the endpoint that already saves the name.
    // Keywords live on the service's own contact, but neither contacts endpoint can
    // write it: a queue account is not an "anumber account colleague", so the contact
    // never resolves as bound and both reject it. updateQueue applies contact
    // keywords when sent, so the two fields stay together. (AC-1942)
    var nameChanged = !!newName && newName !== description;
    if (nameChanged || keywordsChanged) {
      dispatch(updateQueue(entityKey, _objectSpread(_objectSpread({}, nameChanged ? {
        description: newName
      } : {}), keywordsChanged ? {
        contact: {
          keywords: newKeywords
        }
      } : {})));
    }
    history.push(generatePath(routes['pbx.queues.entityKey.settings'], {
      entityKey: entityKey
    }));
  };

  // `editable` and the contact's keywords arrive together, from the single-queue fetch
  // PbxQueueRoutes starts on mount; the queue list does not carry either. Until it lands
  // the user's rights are unknown, and the inputs are uncontrolled, so they would keep
  // whatever they first rendered with.
  if (editable === undefined) {
    return /*#__PURE__*/_jsx(Loading, {});
  }

  // The settings list already hides the row for a service the user may not edit, but
  // the page is reachable by URL, so the flag is checked here too. (PD-2455)
  if (!editable) {
    return /*#__PURE__*/_jsx(Redirect, {
      to: generatePath(routes['pbx.queues.entityKey.settings'], {
        entityKey: entityKey
      })
    });
  }
  return /*#__PURE__*/_jsx(Page, {
    header: "pbx.name",
    onClickBack: onClickBack,
    children: /*#__PURE__*/_jsxs("div", {
      className: "flex w-full flex-col gap-y-6",
      children: [/*#__PURE__*/_jsx(BaseInput, {
        id: NAME_INPUT_ID,
        label: i18next.t('pbx.name'),
        defaultValue: description,
        onChange: function onChange(e) {
          return setNewName(e.target.value);
        }
      }), /*#__PURE__*/_jsx(BaseInput, {
        id: KEYWORDS_INPUT_ID,
        label: i18next.t('pbx.keywords'),
        defaultValue: keywords !== null && keywords !== void 0 ? keywords : '',
        onChange: function onChange(e) {
          return setNewKeywords(e.target.value);
        }
      })]
    })
  });
};
export default PbxSettingsEditName;